import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Shield, FileText, Eye, Paperclip, Download, Image as ImageIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { vehicleService } from '../../services/vehicle.service';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { Vehicle, FuelType, ExpenseCategory, VehicleExpense } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { AttachmentViewer } from '../../components/ui/AttachmentViewer';
import { VehicleFormModal } from './VehicleFormModal';
import toast from 'react-hot-toast';

export const VehiclesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFuelType, setFilterFuelType] = useState<string>('');
  
  // LATRA/Insurance expense modal states
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [expenseType, setExpenseType] = useState<'LATRA' | 'INSURANCE'>('LATRA');
  const [expenseVehicleId, setExpenseVehicleId] = useState<number | null>(null);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [validFrom, setValidFrom] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseAttachment, setExpenseAttachment] = useState<File | null>(null);
  const [, setIsUploading] = useState(false);
  
  // View expenses modal
  const [isViewExpensesModalOpen, setIsViewExpensesModalOpen] = useState(false);
  const [viewingVehicle, setViewingVehicle] = useState<Vehicle | null>(null);
  
  // Attachment viewer state
  const [isAttachmentViewerOpen, setIsAttachmentViewerOpen] = useState(false);
  const [viewingAttachmentExpense, setViewingAttachmentExpense] = useState<VehicleExpense | null>(null);

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: vehicleService.getAll,
  });

  const { data: vehicleExpenses } = useQuery({
    queryKey: ['vehicleExpenses', viewingVehicle?.id],
    queryFn: () => vehicleExpenseService.getByVehicle(viewingVehicle!.id),
    enabled: !!viewingVehicle,
  });

  const createExpenseMutation = useMutation({
    mutationFn: vehicleExpenseService.recordExpense,
    onSuccess: async (createdExpense) => {
      // Upload attachment if provided
      if (expenseAttachment) {
        setIsUploading(true);
        try {
          await vehicleExpenseService.uploadAttachment(createdExpense.id, expenseAttachment);
          toast.success(`${expenseType} expense recorded with attachment`);
        } catch (err) {
          toast.success(`${expenseType} expense recorded (attachment upload failed)`);
        } finally {
          setIsUploading(false);
        }
      } else {
        toast.success(`${expenseType} expense recorded successfully`);
      }
      queryClient.invalidateQueries({ queryKey: ['vehicleExpenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      handleCloseExpenseModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to record expense');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: vehicleService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehicle deactivated successfully');
    },
    onError: () => {
      toast.error('Failed to deactivate vehicle');
    },
  });

  const handleCreate = () => {
    setSelectedVehicle(null);
    setIsModalOpen(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this vehicle?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleOpenExpenseModal = (vehicleId: number, type: 'LATRA' | 'INSURANCE') => {
    setExpenseVehicleId(vehicleId);
    setExpenseType(type);
    setIsExpenseModalOpen(true);
  };

  const handleCloseExpenseModal = () => {
    setIsExpenseModalOpen(false);
    setExpenseVehicleId(null);
    setExpenseAmount('');
    setExpenseDate(new Date().toISOString().split('T')[0]);
    setValidFrom(new Date().toISOString().split('T')[0]);
    setValidUntil('');
    setPolicyNumber('');
    setInsuranceProvider('');
    setExpenseDescription('');
    setExpenseAttachment(null);
  };

  const handleDownloadAttachment = async (expense: VehicleExpense) => {
    try {
      const blob = await vehicleExpenseService.downloadAttachment(expense.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = expense.attachmentFileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Failed to download attachment');
    }
  };

  const handleSubmitExpense = () => {
    if (!expenseVehicleId || !expenseAmount || !validFrom || !validUntil) {
      toast.error('Please fill in all required fields');
      return;
    }

    createExpenseMutation.mutate({
      vehicleId: expenseVehicleId,
      expenseDate,
      category: expenseType === 'LATRA' ? ExpenseCategory.LATRA : ExpenseCategory.INSURANCE,
      amount: parseFloat(expenseAmount),
      validFrom,
      validUntil,
      policyNumber: policyNumber || undefined,
      insuranceProvider: insuranceProvider || undefined,
      description: expenseDescription || `${expenseType} payment`,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const filteredVehicles = vehicles?.filter((vehicle) => {
    const matchesSearch = vehicle.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFuelType = !filterFuelType || vehicle.fuelType === filterFuelType;
    return matchesSearch && matchesFuelType;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">Vehicle Management</h1>
          <p className="text-gray-600">Manage your fleet vehicles</p>
        </div>
        <Button onClick={handleCreate} variant="primary">
          <Plus size={20} className="mr-2" />
          Add Vehicle
        </Button>
      </div>

      <Card>
        <div className="mb-4 flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search by registration, make, or model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <select
            value={filterFuelType}
            onChange={(e) => setFilterFuelType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Fuel Types</option>
            {Object.values(FuelType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading vehicles...</p>
          </div>
        ) : filteredVehicles && filteredVehicles.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Registration Number</TableHead>
                <TableHead>Make & Model</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Fuel Type</TableHead>
                <TableHead>Efficiency (km/L)</TableHead>
                <TableHead>Mileage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVehicles.map((vehicle) => (
                <TableRow key={vehicle.id}>
                  <TableCell className="font-medium">{vehicle.registrationNumber}</TableCell>
                  <TableCell>{vehicle.make} {vehicle.model}</TableCell>
                  <TableCell>{vehicle.year}</TableCell>
                  <TableCell>
                    <Badge variant="info">{vehicle.fuelType}</Badge>
                  </TableCell>
                  <TableCell>{vehicle.fuelEfficiencyKmPerLiter}</TableCell>
                  <TableCell>{vehicle.currentMileage.toLocaleString()} km</TableCell>
                  <TableCell>
                    <Badge variant={vehicle.active ? 'success' : 'error'}>
                      {vehicle.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                        className="p-1 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(vehicle)}
                        className="p-1 text-primary hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenExpenseModal(vehicle.id, 'LATRA')}
                        className="p-1 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                        title="Add LATRA"
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenExpenseModal(vehicle.id, 'INSURANCE')}
                        className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Add Insurance"
                      >
                        <Shield size={16} />
                      </button>
                      {vehicle.active && (
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="p-1 text-error hover:bg-red-50 rounded transition-colors"
                          title="Deactivate"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No vehicles found</p>
          </div>
        )}
      </Card>

      <VehicleFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vehicle={selectedVehicle}
      />

      {/* LATRA/Insurance Expense Modal */}
      <Modal
        isOpen={isExpenseModalOpen}
        onClose={handleCloseExpenseModal}
        title={`Add ${expenseType} Expense`}
        footer={
          <>
            <Button variant="ghost" onClick={handleCloseExpenseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitExpense}
              loading={createExpenseMutation.isPending}
            >
              Record {expenseType}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Amount (TZS)"
            type="number"
            value={expenseAmount}
            onChange={(e) => setExpenseAmount(e.target.value)}
            placeholder="e.g., 500000"
            required
          />
          <Input
            label="Payment Date"
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valid From"
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              required
            />
            <Input
              label="Valid Until"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              required
            />
          </div>
          {expenseType === 'INSURANCE' && (
            <>
              <Input
                label="Policy Number"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="e.g., INS-2024-001"
              />
              <Input
                label="Insurance Provider"
                value={insuranceProvider}
                onChange={(e) => setInsuranceProvider(e.target.value)}
                placeholder="e.g., NIC Insurance"
              />
            </>
          )}
          <Input
            label="Description (Optional)"
            value={expenseDescription}
            onChange={(e) => setExpenseDescription(e.target.value)}
            placeholder={`${expenseType} payment description`}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment (PDF, JPG, PNG)
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setExpenseAttachment(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            {expenseAttachment && (
              <div className="flex items-center gap-2 mt-1">
                <Paperclip size={14} className="text-gray-500" />
                <span className="text-xs text-gray-600">{expenseAttachment.name}</span>
                <button
                  type="button"
                  onClick={() => setExpenseAttachment(null)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* View Vehicle Expenses Modal */}
      <Modal
        isOpen={isViewExpensesModalOpen}
        onClose={() => {
          setIsViewExpensesModalOpen(false);
          setViewingVehicle(null);
        }}
        title={`Expenses - ${viewingVehicle?.registrationNumber || ''}`}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => navigate(`/vehicles/${viewingVehicle?.id}`)}>
            View Full Details
          </Button>
        }
      >
        {vehicleExpenses && vehicleExpenses.length > 0 ? (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicleExpenses
                  .filter((e: VehicleExpense) => 
                    e.category === ExpenseCategory.LATRA || 
                    e.category === ExpenseCategory.INSURANCE ||
                    e.category === ExpenseCategory.MAINTENANCE ||
                    e.category === ExpenseCategory.REPAIR
                  )
                  .map((expense: VehicleExpense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <Badge variant={expense.category === ExpenseCategory.LATRA ? 'warning' : 'info'}>
                          {expense.category}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(expense.amount)}</TableCell>
                      <TableCell>
                        {expense.validUntil ? format(parseISO(expense.validUntil), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={expense.status === 'APPROVED' ? 'success' : 'warning'}>
                          {expense.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {expense.hasAttachment ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setViewingAttachmentExpense(expense);
                                setIsAttachmentViewerOpen(true);
                              }}
                              className="flex items-center gap-1 text-primary hover:text-primary-dark"
                              title="View attachment"
                            >
                              {expense.attachmentContentType?.startsWith('image/') ? (
                                <ImageIcon size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                              <span className="text-xs">View</span>
                            </button>
                            <button
                              onClick={() => handleDownloadAttachment(expense)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Download"
                            >
                              <Download size={14} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No LATRA, Insurance, or Maintenance expenses recorded for this vehicle.
          </div>
        )}
      </Modal>

      {/* Attachment Viewer */}
      {viewingAttachmentExpense && (
        <AttachmentViewer
          expenseId={viewingAttachmentExpense.id}
          fileName={viewingAttachmentExpense.attachmentFileName || 'attachment'}
          contentType={viewingAttachmentExpense.attachmentContentType || 'application/octet-stream'}
          isOpen={isAttachmentViewerOpen}
          onClose={() => {
            setIsAttachmentViewerOpen(false);
            setViewingAttachmentExpense(null);
          }}
          onDownload={() => handleDownloadAttachment(viewingAttachmentExpense)}
        />
      )}
    </div>
  );
};
